import type { UserAgriness, UserPreferences, User } from '@/domain/user/types';
import type { Holding } from '@/domain/holding/types';
import { getStorageItem, removeStorageItem, setStorageItem } from './localStorage';
import { migrateCookieToStorage } from './cookieStorage';
import { clearFresh, readFresh, writeFresh } from './freshCache';
import { clearReportCache } from './reportCache';

const KEY = 'userAgriness';
const KEY_HELP = 'helpUserDismiss';
const KEY_SYNCED = 'userAgrinessSynced';

// Both keys used to be cookies and the `userAgriness` blob no longer fit in one (CORP-8298).
migrateCookieToStorage(KEY);
migrateCookieToStorage(KEY_HELP);

export const userStorageService = {
  getUserAgriness(): UserAgriness | null {
    const raw = getStorageItem(KEY);
    if (!raw) return null;
    try {
      return JSON.parse(raw) as UserAgriness;
    } catch {
      removeStorageItem(KEY);
      return null;
    }
  },
  setUserAgriness(u: UserAgriness): void {
    setStorageItem(KEY, JSON.stringify(u));
  },
  removeUserAgriness(): void {
    removeStorageItem(KEY);
    clearFresh(KEY_SYNCED);
    clearReportCache();
  },
  // CORP-8479: quando o usuário/holdings foram buscados no servidor pela última vez. Enquanto for
  // recente, o app não repete `user-profile/` + `holdings/` no F5.
  markSynced(): void {
    writeFresh(KEY_SYNCED, true);
  },
  isSyncedRecently(): boolean {
    return readFresh<boolean>(KEY_SYNCED) === true;
  },
  getUserPreferences(): UserPreferences | null {
    return this.getUserAgriness()?.userPreferences ?? null;
  },
  getUserProfile(): User | undefined {
    return this.getUserAgriness()?.userProfile;
  },
  getCurrentHolding(): Holding | undefined {
    return this.getUserPreferences()?.currentHolding;
  },
  getCurrentHoldingId(): string | undefined {
    return this.getCurrentHolding()?.id;
  },
  setCurrentHolding(holding: Holding): void {
    const u = this.getUserAgriness();
    if (!u) return;
    u.userPreferences = { ...u.userPreferences, currentHolding: holding };
    this.setUserAgriness(u);
  },
  // The API sends `1 | 0`, so the comparison has to be a coercion — `=== true` never matches.
  isSuperHolding(): boolean {
    return !!this.getCurrentHolding()?.is_super_holding;
  },
  getLanguage(): string | undefined {
    return this.getUserProfile()?.language;
  },
  getDecimalSeparator(): string | undefined {
    return this.getUserProfile()?.decimal_separator;
  },
  getHelpUserDismiss(): boolean {
    return getStorageItem(KEY_HELP) === 'true';
  },
  setHelpUserDismiss(v: boolean): void {
    setStorageItem(KEY_HELP, String(v));
  },
};
